/**
 * Cum apare textul în card: cuvânt cu cuvânt, la ~90ms distanță.
 *
 * Nu e streaming - răspunsul e deja întreg aici - ci ritmul de citire, la fel ca
 * `revealInto` din panoul clasic. Diferă mijlocul: acolo textul crește ca șir,
 * aici fiecare cuvânt e un `<span>` cu propria întârziere de animație, deci
 * derularea e a browserului, nu a unui rAF care rulează cât timp scrie. Pe
 * cardul de sticlă asta contează: forma se măsoară o singură dată pe cuvânt, nu
 * pe fiecare caracter.
 */

/** Distanța dintre cuvinte. Din prototip - sub ~60ms se citește ca un glitch. */
export const WORD_STAGGER_MS = 90;

/** Peste atât derularea ar întârzia cititul în loc să-l ghideze. */
const MAX_REVEAL_MS = 4000;

export function reducedMotion(): boolean {
  return typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function wordSpan(word: string, delayMs: number | null): HTMLSpanElement {
  const span = document.createElement("span");
  span.className = delayMs === null ? "aig-w aig-w--shown" : "aig-w";
  if (delayMs !== null) span.style.animationDelay = `${delayMs}ms`;
  span.textContent = word;
  return span;
}

/**
 * Scrie textul în element, cuvânt cu cuvânt. Întoarce durata totală, ca
 * apelantul să știe când se poate întâmpla următorul lucru (halo-ul de ghidare
 * din faza B apare DUPĂ ultimul cuvânt, nu peste el).
 *
 * `instant` = fără animație: întoarcerea din input la ultima replică nu e o
 * replică nouă, deci nu se rejoacă. La fel sub `prefers-reduced-motion`.
 */
export function revealWords(
  el: HTMLElement,
  text: string,
  opts: { instant?: boolean; hint?: string } = {},
): number {
  const instant = opts.instant || reducedMotion();
  const words = text.split(/\s+/).filter(Boolean);
  // Stagger-ul se strânge dacă răspunsul e lung: ritmul de citire nu are voie să
  // devină o coadă de așteptare.
  const step = instant ? 0 : Math.min(WORD_STAGGER_MS, MAX_REVEAL_MS / Math.max(1, words.length));
  el.replaceChildren();
  words.forEach((word, i) => {
    el.appendChild(wordSpan(word, instant ? null : Math.round(i * step)));
    el.appendChild(document.createTextNode(" "));
  });
  const duration = instant ? 0 : Math.round(words.length * step);
  if (opts.hint) {
    const hint = document.createElement("span");
    hint.className = "aig-hint";
    hint.textContent = opts.hint;
    el.appendChild(hint);
  }
  return duration;
}

/** Întrebarea omului: citată, estompată. El știe ce a spus - e o confirmare. */
export function showQuoted(el: HTMLElement, text: string): void {
  const quoted = document.createElement("span");
  quoted.className = "aig-dim";
  quoted.textContent = `„${text}”`;
  el.replaceChildren(quoted);
}

/** „Mă gândesc": acțiune de sine stătătoare, nu decor peste întrebare. */
export function showThinking(el: HTMLElement, label: string): void {
  const think = document.createElement("span");
  think.className = "aig-think";
  think.textContent = label;
  el.replaceChildren(think);
}

/** Sare peste derularea în curs: toate cuvintele, acum. */
export function finishWords(el: HTMLElement): void {
  el.querySelectorAll(".aig-w").forEach((span) => {
    span.classList.add("aig-w--shown");
    (span as HTMLElement).style.animationDelay = "";
  });
}
